import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  type AriaAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type Ref,
} from "react";
import { useForkRef } from "@mui/material/utils";

import { composeEventHandlers } from "ui/actions-menu/actions-menu.helpers";
import type {
  ActionsMenuProps,
  ActionsMenuTriggerSlotProps,
} from "ui/actions-menu/actions-menu.types";

type UseActionsMenuProps = Pick<
  ActionsMenuProps,
  | "itemName"
  | "open"
  | "onOpen"
  | "onClose"
  | "offsetLeft"
  | "offsetTop"
  | "placement"
  | "menuId"
  | "isSubmenu"
> & {
  /** The call site's own trigger props, so its handlers run first and its `ref` is kept. */
  triggerProps?: ActionsMenuTriggerSlotProps;
};

/**
 * Marks a portalled surface that belongs to an open menu — a callout anchored to one of its rows, say,
 * which has to escape the panel's `overflow` to be seen at all. Such a surface is outside the panel in
 * the DOM, so without this it reads as a click outside, and because that check runs on `mousedown` the
 * press closes the menu and unmounts the surface before it can ever become a click. Its buttons then
 * look alive and do nothing. Put the attribute on the portal root, e.g. the `Popper`.
 */
export const MENU_PORTAL_ATTRIBUTE = "data-actions-menu-portal";

const ARIA_HAS_POPUP: AriaAttributes["aria-haspopup"] = "menu";

export const useActionsMenu = ({
  itemName,
  open,
  onOpen,
  onClose,
  offsetLeft = 0,
  offsetTop = 8,
  placement = "bottom-end",
  menuId = "actions-menu",
  isSubmenu = false,
  triggerProps,
}: UseActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false);

  const buttonRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLElement | null>(null);

  const popperModifiers = useMemo(
    () => [
      { name: "offset", options: { offset: [offsetLeft, offsetTop] } },
      {
        name: "flip",
        options: {
          // A nested panel that flipped to `top-end` would cover the rows of the menu it opened from.
          fallbackPlacements: isSubmenu ? ["left-start"] : ["top-end"],
          padding: 8,
        },
      },
      { name: "preventOverflow", options: { mainAxis: true, altAxis: true, padding: 8 } },
    ],
    [offsetLeft, offsetTop, isSubmenu],
  );

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;

      const element = target instanceof Element ? target : target.parentElement;
      if (element?.closest(`[${MENU_PORTAL_ATTRIBUTE}]`)) return;

      onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    const handleScroll = (event: Event) => {
      const scrollTarget = event.target;

      if (scrollTarget instanceof Node && menuRef.current?.contains(scrollTarget)) {
        return;
      }

      if (scrollTarget instanceof Element && scrollTarget.closest(`[${MENU_PORTAL_ATTRIBUTE}]`)) {
        return;
      }

      onClose();
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [open, onClose]);

  const closeAndRestoreFocus = useCallback(() => {
    onClose();
    buttonRef.current?.focus();
  }, [onClose]);

  /**
   * Escape is bound to the document rather than to the panel because a mouse-opened menu leaves focus
   * on the trigger, so a listener on the list would never see the key.
   *
   * A submenu listens in the capture phase and stops the event there, so Escape closes only the
   * innermost panel. Otherwise the enclosing menu, registered first, would close too and unmount the
   * row this submenu hands focus back to, dropping focus on `<body>`.
   */
  useEffect(() => {
    if (!open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      if (isSubmenu) event.stopPropagation();
      closeAndRestoreFocus();
    };

    document.addEventListener("keydown", handleEscape, isSubmenu);
    return () => document.removeEventListener("keydown", handleEscape, isSubmenu);
  }, [open, closeAndRestoreFocus, isSubmenu]);

  /**
   * Arrow keys, Home/End and typeahead belong to `MenuList`; the only key left to us is the one that
   * takes focus back out of the menu. Tab returns it to the trigger rather than jumping to whatever
   * follows the trigger in the document, which is the one behaviour this hook gave up when it stopped
   * scanning the page for focusable elements.
   */
  const handleListKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      const isSubmenuBack = isSubmenu && event.key === "ArrowLeft";
      if (event.key !== "Tab" && !isSubmenuBack) return;

      event.preventDefault();
      closeAndRestoreFocus();
    },
    [closeAndRestoreFocus, isSubmenu],
  );

  const buttonRefCallback = useCallback((node: HTMLElement | null) => {
    setAnchorEl(node);
    buttonRef.current = node;
  }, []);

  /**
   * A callback rather than the ref itself: `MenuList` is a plain `forwardRef<HTMLUListElement>`, so it
   * does not narrow its ref type for `component="div"` the way a polymorphic component would.
   */
  const menuRefCallback = useCallback((node: HTMLElement | null) => {
    menuRef.current = node;
  }, []);

  const handleTriggerClick = () => {
    setOpenedByKeyboard(false);
    if (open) onClose();
    else onOpen();
  };

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " " || (isSubmenu && event.key === "ArrowRight")) {
      event.preventDefault();
      setOpenedByKeyboard(true);
      onOpen();
    }
  };

  const triggerRef = useForkRef(triggerProps?.ref, buttonRefCallback);

  const triggerSlotProps = {
    "aria-label": `Actions for ${itemName}`,
    "aria-haspopup": ARIA_HAS_POPUP,
    "aria-expanded": open,
    "aria-controls": open ? menuId : undefined,
    onClick: composeEventHandlers<ReactMouseEvent<HTMLElement>>(
      triggerProps?.onClick,
      handleTriggerClick,
    ),
    onKeyDown: composeEventHandlers(triggerProps?.onKeyDown, handleTriggerKeyDown),
    ref: triggerRef,
  };

  return {
    menuId,
    menuLabel: `Actions menu for ${itemName}`,
    menuRefCallback,
    anchorEl,
    placement,
    popperModifiers,
    triggerSlotProps,
    openedByKeyboard,
    handleListKeyDown,
  };
};
